import { BarChart, Bell, LogOut, Train, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";

import { getNavigationForRole } from "../lib/utils";

import { PresentationLink } from "./PresentationLink";
import { UserGuideLink } from "./UserGuideLink";

import { Badge, Button } from "@/common/components";
import { ROUTES } from "@/common/config";
import { useAuth, useUser } from "@/common/hooks";
import { cn } from "@/common/lib";
import type { Notification } from "@/common/model/schemas/replit";
import "./styles.css";

export function MobileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [location, setLocation] = useLocation();

  const {
    auth: { isAuthenticated, role },
    logout,
  } = useAuth();
  const { permissions } = useUser();

  const navigationList = getNavigationForRole(role, permissions);

  // Закрывать меню при смене роута
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Блокируем прокрутку боди, когда меню открыто
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  // Закрытие по Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const notifications: Notification[] = [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <>
      {/* Top bar + sidebar (только мобилка) */}
      <div
        className={`md:hidden ${isAuthenticated ? "relative h-20" : "absolute h-28"} top-0 z-40 w-full bg-transparent`}
      >
        <div className="flex items-center justify-between px-4 py-3 h-full">
          <div className="flex items-center">
            <div className="h-20 mr-4 flex items-center justify-center">
              <img src="/logo-head.png" alt="ЭИС" className="h-10 object-contain" />
            </div>
          </div>

          {isAuthenticated ? (
            <>
              {permissions?.profile.vew_profile && (
                <Link href={ROUTES.PROFILE}>
                  <Button
                    variant="outline"
                    className={`flex items-center gap-2 text-black hover:bg-black/10`}
                  >
                    <User className="h-4 w-4" />
                    Профиль
                  </Button>
                </Link>
              )}

              <div className="flex justify-center gap-2">
                <Link href={ROUTES.NOTIFICATIONS}>
                  <Button
                    variant="ghost"
                    className={`relative p-2 h-10 w-10 "text-black hover:bg-black/10"`}
                  >
                    <Bell className="h-6 w-6" />
                    {unreadCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="absolute -top-1 -right-1 h-5 w-5 text-xs p-0 flex  bg-red-500"
                      >
                        {unreadCount}
                      </Badge>
                    )}
                  </Button>
                </Link>

                {isOpen ? (
                  <button
                    aria-label="Закрыть меню"
                    onClick={() => {
                      setIsOpen(false);
                    }}
                    className="p-2 rounded-md hover:bg-gray-100 active:bg-gray-200 transition"
                  >
                    <X className="h-6 w-6 text-gray-800" />
                  </button>
                ) : (
                  <button
                    aria-label="Открыть меню"
                    aria-expanded={isOpen}
                    aria-controls="mobile-drawer"
                    onClick={() => setIsOpen(true)}
                    className="p-2 rounded-md hover:bg-gray-100 active:bg-gray-200 transition"
                  >
                    <BarChart className="h-6 w-6 text-gray-800 -rotate-90" />
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <PresentationLink classNameBtn="h-9 px-3 text-xs sm:px-5 sm:text-sm" />
              <UserGuideLink classNameBtn="h-9 px-3 text-xs sm:px-5 sm:text-sm" />
            </div>
          )}
        </div>
      </div>
      {/* Overlay + Drawer */}
      <div
        className={cn(
          "fixed top-20 right-0 bottom-0 left-0 z-50 md:hidden",
          isOpen ? "" : "pointer-events-none",
        )}
      >
        {/* Overlay */}
        <div
          className={cn(
            "absolute inset-0 bg-black/40 transition-opacity",
            isOpen ? "opacity-100" : "opacity-0",
          )}
          onClick={() => setIsOpen(false)}
        />
        {/* Drawer */}
        <aside
          id="mobile-drawer"
          className={cn(
            "absolute inset-0 h-full w-full shadow-xl transition-transform",
            isOpen ? "translate-x-0" : "-translate-x-full",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Навигация */}
          <nav
            className="px-4 pt-5 pb-6 h-full flex flex-col"
            style={{ backgroundColor: "var(--color-background-white)" }}
          >
            <div className="pb-5 border-b border-gray-200">
              <p className="text-lg italic font-serif text-orange-600 leading-[26px]">
                Быстро, выгодно, безопасно
              </p>
            </div>

            <ul className="space-y-3 mt-[10px]">
              {navigationList.map((item) => {
                const Icon = item.icon;
                const isActive = location === item.href;
                const isBorderTop = item.href === ROUTES.CREATE_ORDER;
                return (
                  <li
                    key={item.name}
                    className={cn(isBorderTop && "border-t border-gray-200 pt-5")}
                  >
                    <Link href={item.href}>
                      <span
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          "flex items-center px-3 py-2 text-lg leading-[22px] font-medium rounded-md transition-colors cursor-pointer",
                          isActive
                            ? "nav-active"
                            : item.href === ROUTES.CREATE_ORDER
                              ? "bg-orange-600 hover:bg-orange-700 text-white"
                              : "nav-item",
                        )}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        {item.name}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Button
              variant="outline"
              onClick={logout}
              className="flex items-center gap-3 w-full mt-auto text-lg h-11"
            >
              <LogOut className="h-4 w-4" />
              Выйти
            </Button>
          </nav>
        </aside>
      </div>
    </>
  );
}
